import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import BackdropCircularProgress from '../../components/BackdropCircularProgress';
import JobCreationFAQ from '../../components/FAQcomponent';
import FormCloseButton from '../../components/Form/FormCloseButton';
import selectUser from '../../store/selectors/appSelector';
import { showSnackbar } from '../app';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import CreateJobFormComponent from './CreateJobCard/CreateJobFormComponent';
import { FAQ_LIST } from './constDicts';
import { getExtensionGroupPayload } from './helpers';
import { clearGroupError, clearPostGroupOrdersResponse, postGroupOrders } from './jobCreateReducer';
import {
  selectErrorPostGroupOrders,
  selectIsProcessingPostGroupOrders,
  selectPostGroupOrdersResponse,
} from './jobCreateSelectors';

const JobOrdersCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const theme = useTheme();

  const isProcessingPostGroupOrders = useSelector(selectIsProcessingPostGroupOrders);
  const error = useSelector(selectErrorPostGroupOrders);
  const createResponse = useSelector(selectPostGroupOrdersResponse);

  const [jobName, setJobName] = useState(null);

  const submitFunc = (pa) => {
    const createPayload = getExtensionGroupPayload({ formValues: pa });
    setJobName(pa?.positionTemplate?.name);
    dispatch(postGroupOrders(createPayload));
  };

  const closeAndClear = useCallback(
    (message) => {
      navigate(`/${ROUTE_CONSTANTS.JOB_ORDERS}`);
      dispatch(clearPostGroupOrdersResponse());
      dispatch(showSnackbar({ message }));
    },
    [dispatch, navigate]
  );

  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const mainWrapperSx = {
    display: 'flex',
    padding: theme.spacing(2),
    flexDirection: smallScreen ? 'column' : 'row',
    gap: theme.spacing(2),
  };

  const message = LanguageConverter('job.create.success', jobName);

  useEffect(() => {
    const jobOrderIds = createResponse?.data?.jobOrderIds;
    const hasJobOrderIds = Array.isArray(jobOrderIds) && jobOrderIds.length > 0;
    const hasExtensionGroup = Boolean(
      createResponse?.data?.extensionGroup?._id || createResponse?.data?.extensionGroup
    );
    if (hasJobOrderIds || hasExtensionGroup) {
      closeAndClear(message);
    }
  }, [closeAndClear, createResponse, dispatch, jobName, message]);

  useEffect(() => {
    if (error) {
      dispatch(showSnackbar({ message: error }));
      dispatch(clearGroupError());
    }
  }, [dispatch, error]);

  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 10,
        width: '100vw',
        padding: theme.spacing(2),
      }}
    >
      {isProcessingPostGroupOrders && <BackdropCircularProgress />}
      <FormCloseButton onClose={closeAndClear} message="jobs.backToList" />
      {createResponse === null && (
        <Box sx={mainWrapperSx}>
          <Box
            sx={{
              flex: 5,
            }}
          >
            <Typography
              sx={{
                color: theme?.jobOrders?.createPage?.addJobHeaderTypography?.fontColor,
                fontSize: theme?.jobOrders?.createPage?.addJobHeaderTypography?.fontSize,
                fontFamily: theme?.jobOrders?.createPage?.addJobHeaderTypography?.font,
                marginBottom: theme.spacing(1),
              }}
            >
              {LanguageConverter('job.create')}
            </Typography>
            <CreateJobFormComponent
              user={user}
              onSubmit={submitFunc}
              onClose={closeAndClear}
              isProcessing={isProcessingPostGroupOrders}
            />
          </Box>

          <Box sx={{ flex: 2 }}>
            <JobCreationFAQ faqList={FAQ_LIST} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default JobOrdersCreate;
